import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { MaskDirective } from 'ngx-mask';
import { ApiService } from 'src/app/services/api/api.service';

import utils, { BrValidators } from '../../helpers/utils';
import {
  applyDocumentMask,
  CNPJ_MASK,
  CPF_MASK,
  DOCUMENT_MASK,
  DocumentMaskType,
  isCnpjDocument,
  isCpfDocument,
  isDocumentComplete,
  maskCnpj,
  maskDocument,
  normalizeDocumentForApi,
} from '../../helpers/document';

@Component({
  selector: 'app-masked-input',
  templateUrl: './masked-input.component.html',
  styleUrls: ['./masked-input.component.scss'],
})
export class MaskedInputComponent implements OnInit {
  @Input() id?: string = utils.makeID();
  @Input() name?: string;
  @Input() placeholder: string = 'Digite';
  @Input() label?: string;
  @Input() type = 'text';
  @Input() max?: number;

  @Input() standalone: boolean = false;
  @Input() value: string = '';

  @Input() controlName: string = '';
  @Input() group?: any = new FormGroup({});

  @Input() helpText?: string;
  @Input() errorText: string = 'Campo inválido';

  @Input() readOnly: boolean = false;
  @Input() validated: boolean = false;
  @Input() rows: number = 0;

  @Input() isUpperCase = false;

  @Input() isDocument: boolean = false;

  /** Quando definido, aplica máscara custom (suporta CNPJ alfanumérico). */
  @Input() documentType?: DocumentMaskType;

  @Input() button: string = '';

  @Input() mask?: string;
  @Input() usePatterns: boolean = false;

  @Input() prefix: string = '';
  @Input() suffix: string = '';

  @Input() hasError: boolean = false;
  @Input() alwaysValid: boolean = false;
  @Input() skipValidation: boolean = false;

  @Input() showRequiredIndicator: boolean = true;

  @Output() onButtonClick: EventEmitter<MouseEvent> = new EventEmitter();

  @Output() onChange: EventEmitter<string> = new EventEmitter();

  pattern: MaskDirective['patterns'] = {
    W: {
      pattern: new RegExp('[A-Za-záàâãéèêíïóôõöúçñÁÀÂÃÉÈÍÏÓÔÕÖÚÇÑ ]'),
    },
    U: {
      pattern: new RegExp('[A-Z]'),
    },
    L: {
      pattern: new RegExp('[a-z]'),
    },
    N: {
      pattern: new RegExp('[0-9]'),
    },
    A: {
      pattern: new RegExp('[A-Z0-9]'),
    },
  };

  error: boolean = false;

  /** Mantém modo CNPJ alfanumérico após primeira letra em campos cpfOrCnpj. */
  private alphanumericCnpjMode = false;

  constructor(private apiService: ApiService) {}

  get useDocumentMasking(): boolean {
    return !!this.documentType || this.isLegacyDocumentMask();
  }

  get effectiveMask(): string {
    if (this.useDocumentMasking) {
      return '';
    }
    return this.mask ?? '';
  }

  get documentMaxLength(): number {
    return this.max ?? 18;
  }

  get documentInputClass(): string {
    const docType = this.resolveDocumentType();
    return docType === 'cnpj' || docType === 'cpfOrCnpj'
      ? 'document-uppercase'
      : '';
  }

  isLegacyDocumentMask(): boolean {
    return (
      this.isDocument ||
      this.mask === CNPJ_MASK ||
      this.mask === '00.000.000/0000-00' ||
      this.mask === CPF_MASK ||
      this.mask === '000.000.000-00' ||
      this.mask === DOCUMENT_MASK ||
      this.mask === 'CPF_CNPJ'
    );
  }

  resolveDocumentType(): DocumentMaskType {
    if (this.documentType) {
      return this.documentType;
    }

    if (
      this.mask === CNPJ_MASK ||
      this.mask === '00.000.000/0000-00'
    ) {
      return 'cnpj';
    }

    if (this.mask === CPF_MASK || this.mask === '000.000.000-00') {
      return 'cpf';
    }

    if (this.isDocument) {
      return 'cpfOrCnpj';
    }

    return 'cpfOrCnpj';
  }

  onButtonClicked(event: MouseEvent): void {
    this.onButtonClick.emit(event);
  }

  handleChange(event: any): void {
    this.onChange.emit(event.target.value);
  }

  handleDocumentInput(event: Event): void {
    if (!this.useDocumentMasking || this.readOnly) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const rawValue = input.value ?? '';

    if (!rawValue) {
      this.alphanumericCnpjMode = false;
    } else if (/[a-zA-Z]/.test(rawValue)) {
      this.alphanumericCnpjMode = true;
    }

    const masked = this.applyDocumentMaskValue(rawValue);

    if (input.value !== masked) {
      input.value = masked;
    }

    if (this.standalone) {
      this.value = masked;
      this.onChange.emit(masked);
      return;
    }

    const control = this.group?.controls[this.controlName];
    if (control && control.value !== masked) {
      control.setValue(masked, { emitEvent: true });
    }

    this.onChange.emit(masked);
  }

  private applyDocumentMaskValue(value: string): string {
    const docType = this.resolveDocumentType();

    if (docType === 'cpfOrCnpj' && this.alphanumericCnpjMode) {
      return maskCnpj(value);
    }

    if (docType === 'cpfOrCnpj') {
      return maskDocument(value);
    }

    return applyDocumentMask(value, docType);
  }

  ngOnInit(): void {
    if (this.useDocumentMasking) {
      const initialValue = this.group.controls[this.controlName].value;
      if (initialValue) {
        if (/[a-zA-Z]/.test(String(initialValue))) {
          this.alphanumericCnpjMode = true;
        }
        const maskedInitial = this.applyDocumentMaskValue(String(initialValue));
        if (maskedInitial !== initialValue) {
          this.group.controls[this.controlName].setValue(maskedInitial, {
            emitEvent: false,
          });
        }
      }
    }

    if (this.isDocument) {
      const initialValue = this.group.controls[this.controlName].value;
      if (initialValue) {
        const maskedInitial = applyDocumentMask(
          initialValue,
          this.resolveDocumentType(),
        );

        if (isDocumentComplete(maskedInitial)) {
          setTimeout(() => {
            this.documentInUse(normalizeDocumentForApi(maskedInitial));
          }, 100);
        }
      }

      this.group.controls[this.controlName].valueChanges.subscribe(
        (values: any) => {
          if (!values) {
            return;
          }

          if (isDocumentComplete(values)) {
            this.documentInUse(normalizeDocumentForApi(values));
            return;
          }

          const currentErrors = this.group.controls[this.controlName].errors;
          if (
            currentErrors &&
            (currentErrors['invalidDocument'] || currentErrors['documentInUse'])
          ) {
            const unmaskedLength = normalizeDocumentForApi(values).length;
            if (unmaskedLength < 11) {
              this.error = false;
              const errors = { ...currentErrors };
              delete errors['invalidDocument'];
              delete errors['documentInUse'];
              if (Object.keys(errors).length === 0) {
                this.group.controls[this.controlName].setErrors(null);
              } else {
                this.group.controls[this.controlName].setErrors(errors);
              }
            }
          }
        },
      );
    }
  }

  getClasses(): string {
    if (this.skipValidation) {
      return '';
    }

    if (this.error) {
      return 'error';
    }

    if (this.validated) {
      return 'validated';
    }

    return '';
  }

  getRequired(): boolean {
    const control = this.group.controls[this.controlName];

    if (control && control.validator) {
      let required = false;

      const validator = control.validator({} as AbstractControl);

      if (validator && validator.required) {
        required = true;
      }

      return required;
    }

    return false;
  }

  getTouched(): boolean {
    if (this.standalone) {
      return true;
    } else {
      let touched = false;

      if (this.readOnly) return touched;

      if (this.group) {
        const control = this.group.controls[this.controlName];

        if (control) {
          if (control.touched) {
            touched = true;
          }
        } else {
          console.error(`Control of ${this.controlName} not found!`);
        }
      }

      return touched;
    }
  }

  getInvalid(): boolean {
    if (this.skipValidation) {
      return false;
    }

    if (this.standalone) {
      return this.hasError;
    } else {
      let invalid = false;

      if (this.readOnly) return invalid;

      if (this.group) {
        const control = this.group.controls[this.controlName];

        if (control.invalid) {
          invalid = true;
        }
      }

      return invalid;
    }
  }

  async documentInUse(document: string) {
    if (!document || document.length === 0) {
      return;
    }

    const normalized = normalizeDocumentForApi(document);
    const isCpf = isCpfDocument(normalized);
    const isCnpj = isCnpjDocument(normalized);

    let isValid = false;
    let validationError = '';

    if (isCpf) {
      isValid = BrValidators.cpf(document);
      if (!isValid) {
        validationError = 'CPF inválido';
      }
    } else if (isCnpj) {
      isValid = BrValidators.cnpj(document);
      if (!isValid) {
        validationError = 'CNPJ inválido';
      }
    } else {
      return;
    }

    if (!isValid && validationError) {
      this.errorText = validationError;
      this.error = true;
      this.group.controls[this.controlName].setErrors({
        invalidDocument: true,
      });
      return;
    }

    try {
      const res = await this.apiService.post<ValidateFieldResponse>({
        route: 'validate-document-exists/',
        body: { document: normalized },
      });

      if (res.exists) {
        this.errorText = 'Este documento já está cadastrado';
        this.error = true;
        this.group.controls[this.controlName].setErrors({
          documentInUse: true,
        });
      } else {
        const currentErrors = this.group.controls[this.controlName].errors;
        if (currentErrors && currentErrors['documentInUse']) {
          delete currentErrors['documentInUse'];
          if (Object.keys(currentErrors).length === 0) {
            this.group.controls[this.controlName].setErrors(null);
          } else {
            this.group.controls[this.controlName].setErrors(currentErrors);
          }
        }
        if (currentErrors && currentErrors['invalidDocument']) {
          delete currentErrors['invalidDocument'];
          if (Object.keys(currentErrors).length === 0) {
            this.group.controls[this.controlName].setErrors(null);
          } else {
            this.group.controls[this.controlName].setErrors(currentErrors);
          }
        }
        this.error = false;
      }
    } catch (error) {
      this.error = false;
      const currentErrors = this.group.controls[this.controlName].errors;
      if (currentErrors && currentErrors['invalidDocument']) {
        return;
      }
      this.group.controls[this.controlName].setErrors(null);
    }
  }

  getError(): boolean {
    if (this.alwaysValid || this.skipValidation) return false;

    if (this.standalone) {
      return this.getInvalid();
    }

    const control = this.group?.controls[this.controlName];
    return control && (control.dirty || control.touched) && this.getInvalid();
  }

  getErrorMessage(): string {
    if (this.standalone) {
      return this.errorText;
    }

    if (!this.group) {
      return this.errorText;
    }

    const control = this.group.controls[this.controlName];
    if (!control || !control.errors) {
      return this.errorText;
    }

    const errors = control.errors;

    if (errors['invalidDocument']) {
      return this.errorText || 'Documento inválido';
    }
    if (errors['documentInUse']) {
      return this.errorText || 'Este documento já está cadastrado';
    }
    if (errors['fullNameIncomplete']) {
      return 'O nome deve estar completo';
    }
    if (errors['fullNameTitle']) {
      return 'Não é permitido usar títulos ou saudações no nome';
    }
    if (errors['fullNameAbbreviated']) {
      return 'Não é permitido usar abreviações no nome';
    }
    if (errors['fullNameNumbers']) {
      return 'O nome não pode conter números';
    }
    if (errors['fullName']) {
      return 'Nome completo inválido';
    }

    if (errors['cpf']) {
      return 'CPF inválido';
    }

    if (errors['cnpj']) {
      return 'CNPJ inválido';
    }

    if (errors['invalid']) {
      return 'Documento inválido';
    }

    if (errors['email']) {
      return 'Email inválido';
    }

    if (errors['telefone'] || errors['celular'] || errors['telefoneFixo']) {
      return 'Telefone inválido';
    }

    if (errors['celularDDIDuplicated']) {
      return 'O número não pode conter DDI duplicado';
    }
    if (errors['celularRepeated']) {
      return 'Número de celular inválido';
    }

    if (errors['invalidDDI']) {
      return 'Número inválido';
    }

    if (errors['invalidPhoneLength']) {
      return 'Número de telefone inválido';
    }

    if (errors['invalidPhonePattern']) {
      return 'Número de telefone inválido';
    }

    if (errors['cep']) {
      return 'CEP inválido';
    }

    if (errors['age']) {
      return 'É necessário ser maior de 18 anos';
    }

    if (errors['date']) {
      return 'Data inválida';
    }

    if (errors['required']) {
      return 'Campo obrigatório';
    }

    if (errors['max']) {
      if (this.controlName === 'foundation_date') {
        return 'A data da fundação não pode ser uma data futura';
      }
      if (this.controlName === 'max_term_validity') {
        return 'A validade do mandato não pode ultrapassar 100 anos a partir de hoje';
      }
      return 'A data não pode ser futura';
    }

    if (errors['minConstitutionDate']) {
      return 'A data não pode ser menor que a data de constituição do fundo';
    }

    if (errors['min']) {
      if (this.errorText && this.errorText !== 'Campo inválido') {
        return this.errorText;
      }
      return 'Data inválida';
    }

    if (errors['minlength']) {
      return `Mínimo de ${errors['minlength'].requiredLength} caracteres`;
    }

    if (errors['maxlength']) {
      return `Máximo de ${errors['maxlength'].requiredLength} caracteres`;
    }

    if (errors['pattern']) {
      if (this.isDocument) {
        return 'Documento inválido';
      }
      return 'Formato inválido';
    }

    if (errors['mask']) {
      if (this.isDocument) {
        return 'Documento inválido';
      }
      return 'Formato inválido';
    }

    if (errors['cepIncomplete']) {
      return 'CEP incompleto';
    }

    return this.errorText;
  }
}
